import { useCallback, useEffect, useRef } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { useShallow } from 'zustand/react/shallow';
import { useShellStore } from '../stores/shellStore';

const EDGE_THRESHOLD = 20; // px from left edge to start tracking
const SWIPE_MIN_DISTANCE = 50; // px rightward to trigger open
const CLOSE_SWIPE_MIN_DISTANCE = 50; // px leftward to trigger close

export function useSwipeToOpenSidebar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { openSidebar, closeSidebar } = useShellStore(
    useShallow(state => ({
      openSidebar: state.openSidebar,
      closeSidebar: state.closeSidebar,
    }))
  );

  // Use a ref for sidebarOpen so handlePointerDown doesn't depend on it,
  // keeping the callback stable and avoiding listener churn.
  const sidebarOpenRef = useRef(useShellStore.getState().sidebarOpen);
  useEffect(
    () =>
      useShellStore.subscribe(state => {
        sidebarOpenRef.current = state.sidebarOpen;
      }),
    []
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number | null>(null);
  const startYRef = useRef<number | null>(null);
  const trackingRef = useRef<'open' | 'close' | null>(null);

  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      if (!isMobile) return;

      startXRef.current = e.clientX;
      startYRef.current = e.clientY;

      if (!sidebarOpenRef.current && e.clientX <= EDGE_THRESHOLD) {
        trackingRef.current = 'open';
      } else if (sidebarOpenRef.current) {
        trackingRef.current = 'close';
      } else {
        trackingRef.current = null;
      }
    },
    [isMobile]
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (
        !trackingRef.current ||
        startXRef.current === null ||
        startYRef.current === null
      )
        return;

      const dx = e.clientX - startXRef.current;
      const dy = e.clientY - startYRef.current;

      // If vertical movement exceeds horizontal, this is a scroll — stop tracking
      if (Math.abs(dy) > Math.abs(dx)) {
        trackingRef.current = null;
        return;
      }

      if (trackingRef.current === 'open' && dx >= SWIPE_MIN_DISTANCE) {
        openSidebar();
        trackingRef.current = null;
        startXRef.current = null;
      } else if (
        trackingRef.current === 'close' &&
        dx <= -CLOSE_SWIPE_MIN_DISTANCE
      ) {
        closeSidebar();
        trackingRef.current = null;
        startXRef.current = null;
      }
    },
    [openSidebar, closeSidebar]
  );

  const handlePointerUp = useCallback(() => {
    trackingRef.current = null;
    startXRef.current = null;
    startYRef.current = null;
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isMobile) return;

    el.addEventListener('pointerdown', handlePointerDown, { passive: true });
    el.addEventListener('pointermove', handlePointerMove, { passive: true });
    el.addEventListener('pointerup', handlePointerUp, { passive: true });
    el.addEventListener('pointercancel', handlePointerUp, { passive: true });

    return () => {
      el.removeEventListener('pointerdown', handlePointerDown);
      el.removeEventListener('pointermove', handlePointerMove);
      el.removeEventListener('pointerup', handlePointerUp);
      el.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isMobile, handlePointerDown, handlePointerMove, handlePointerUp]);

  return containerRef;
}
